import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useApp } from "@/contexts/AppContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sun, Moon, MapPin, Search, Ticket, User, LogOut, ScanLine, Calendar, ShieldCheck, Bell, Sparkles, HelpCircle, BookOpen } from "lucide-react";
import BrandMark from "@/components/BrandMark";

const DEFAULT_SEASONAL_CAMPAIGN = {
  enabled: true,
  nav_label: "Navratri",
  label: "Navratri 2026",
  route: "/navratri",
};

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { city, setCity, cities } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [seasonalCampaign, setSeasonalCampaign] = useState(DEFAULT_SEASONAL_CAMPAIGN);
  const unread = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      return;
    }
    let alive = true;
    const load = () => api.get("/notifications")
      .then((r) => { if (alive) setNotifications(r.data || []); })
      .catch(() => {});
    load();
    const timer = setInterval(load, 30000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [user]);

  useEffect(() => {
    api.get("/app-options/seasonal_campaign")
      .then((r) => setSeasonalCampaign({ ...DEFAULT_SEASONAL_CAMPAIGN, ...(r.data?.value || {}) }))
      .catch(() => setSeasonalCampaign(DEFAULT_SEASONAL_CAMPAIGN));
  }, []);

  const openNotification = async (notification) => {
    if (!notification.read) {
      await api.post(`/notifications/${notification.notification_id}/read`).catch(() => {});
      setNotifications((rows) => rows.map((n) => (
        n.notification_id === notification.notification_id ? { ...n, read: true } : n
      )));
    }
    const payloadRoute = notification.data?.route;
    if (typeof payloadRoute === "string" && payloadRoute.startsWith("/")) {
      navigate(payloadRoute);
    } else if (notification.type === "admin_event_review") {
      navigate(`/admin-panel?tab=pending${notification.event_id ? `&event=${notification.event_id}` : ""}`);
    } else if (["event_rejected", "event_needs_changes"].includes(notification.type)) {
      navigate("/my-events");
    } else if (notification.event_id) {
      navigate(`/organiser/${notification.event_id}`);
    } else {
      navigate("/profile");
    }
  };

  return (
    <header className="sticky top-0 z-40 glass" data-testid="navbar">
      <div className="page-shell h-16 flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <BrandMark size={36} />
          <span className="font-display text-lg font-extrabold tracking-tight hidden sm:block">
            NamasteNetwork<span className="text-primary">.ai</span>
          </span>
        </Link>

        {/* City picker */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="city-picker-trigger"
              className="ml-1 flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-sm hover:bg-muted transition"
            >
              <MapPin className="h-4 w-4 text-primary" />
              <span className="font-semibold">{city}</span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48">
            <DropdownMenuLabel>Choose your city</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {cities.map((c) => (
              <DropdownMenuItem
                key={c}
                data-testid={`city-option-${c}`}
                onClick={() => setCity(c)}
                className={c === city ? "font-bold text-primary" : ""}
              >
                {c}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="flex-1" />

        <button
          onClick={() => navigate("/search")}
          data-testid="search-trigger"
          className="hidden md:flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted transition"
        >
          <Search className="h-4 w-4" />
          Search movies, events…
        </button>

        {seasonalCampaign.enabled !== false && (
          <button
            onClick={() => navigate(seasonalCampaign.route || "/navratri")}
            data-testid="seasonal-trigger"
            className="hidden lg:flex items-center gap-2 rounded-full border border-primary/40 px-3 py-1.5 text-sm font-bold text-primary hover:bg-primary/10 transition"
          >
            <Sparkles className="h-4 w-4" />
            {seasonalCampaign.nav_label || seasonalCampaign.label || "Seasonal"}
          </button>
        )}

        <button
          onClick={() => navigate("/creators")}
          data-testid="creators-trigger"
          className="hidden lg:flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted transition"
        >
          <Sparkles className="h-4 w-4" />
          Creators
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full" aria-label="Help and guides" title="Help and guides">
              <HelpCircle className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onClick={() => navigate("/faq")}>
              <HelpCircle className="mr-2 h-4 w-4" /> Frequently asked questions
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/organiser-guide")}>
              <BookOpen className="mr-2 h-4 w-4" /> Organiser guide
            </DropdownMenuItem>
            {user?.role === "admin" && (
              <DropdownMenuItem onClick={() => navigate("/admin-panel?tab=knowledge")}>
                <ShieldCheck className="mr-2 h-4 w-4" /> Admin knowledge base
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          variant="ghost"
          size="icon"
          onClick={toggle}
          data-testid="theme-toggle"
          className="rounded-full"
          aria-label="Toggle theme"
        >
          {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>

        {user ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="relative rounded-full">
                <Avatar className="h-9 w-9 ring-1 ring-border">
                  <AvatarImage src={user.picture} />
                  <AvatarFallback>{(user.name || "U").slice(0, 1)}</AvatarFallback>
                </Avatar>
                {unread > 0 && (
                  <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-[10px] font-black text-primary-foreground">
                    {unread > 9 ? "9+" : unread}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel className="flex items-center justify-between gap-3">
                <span className="truncate">{user.name}</span>
                {unread > 0 && <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-black text-primary-foreground">{unread} new</span>}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                <Bell className="h-3.5 w-3.5" />
                Notifications
              </DropdownMenuLabel>
              {notifications.length === 0 ? (
                <DropdownMenuItem disabled className="text-xs text-muted-foreground">No notifications yet</DropdownMenuItem>
              ) : notifications.slice(0, 4).map((notification) => (
                <DropdownMenuItem
                  key={notification.notification_id}
                  onClick={() => openNotification(notification)}
                  className={`items-start gap-3 py-2 ${notification.read ? "" : "bg-primary/10"}`}
                >
                  <span className={`mt-1 h-2 w-2 rounded-full ${notification.read ? "bg-muted" : "bg-primary"}`} />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-bold">{notification.title}</span>
                    <span className="line-clamp-2 text-xs text-muted-foreground">{notification.body}</span>
                  </span>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                <User className="h-4 w-4 mr-2" /> Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/bookings")} data-testid="menu-bookings">
                <Ticket className="h-4 w-4 mr-2" /> My Bookings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/my-events")} data-testid="menu-my-events">
                <Calendar className="h-4 w-4 mr-2" /> My Events
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/creators")} data-testid="menu-creators">
                <Sparkles className="h-4 w-4 mr-2" /> Creators
              </DropdownMenuItem>
              {seasonalCampaign.enabled !== false && (
                <DropdownMenuItem onClick={() => navigate(seasonalCampaign.route || "/navratri")} data-testid="menu-seasonal">
                  <Sparkles className="h-4 w-4 mr-2" /> {seasonalCampaign.nav_label || seasonalCampaign.label || "Seasonal"}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => navigate("/create-event")} data-testid="menu-create-event">
                <Calendar className="h-4 w-4 mr-2" /> Create Event
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/scanner")} data-testid="menu-scanner">
                <ScanLine className="h-4 w-4 mr-2" /> Scan Tickets
              </DropdownMenuItem>
              {user.role === "admin" && (
                <DropdownMenuItem onClick={() => navigate("/admin-panel")} data-testid="menu-admin">
                  <ShieldCheck className="h-4 w-4 mr-2" /> Admin Panel
                  {unread > 0 && (
                    <span className="ml-auto rounded-full bg-primary px-2 py-0.5 text-[10px] font-black text-primary-foreground">
                      {unread > 9 ? "9+" : unread}
                    </span>
                  )}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                <LogOut className="h-4 w-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button onClick={() => navigate("/login", { state: { from: location.pathname } })} data-testid="navbar-login-btn" className="rounded-full">
            Sign in
          </Button>
        )}
      </div>
    </header>
  );
}
